import { DisplayItem } from "@/components/display-item";
import { ContributorPositionItemView } from "@/entities/contributor-position/views/contributor-position-item-view";
import { ContributorRoleItemView } from "@/entities/contributor-role/views/contributor-role-item-view";
import { Contributor } from "@/generated/raid";
import { Divider, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { memo } from "react";
import { OrcidButton } from "@/components/orcid-button";
import { useResolvedContributorName } from "@/hooks/useResolvedContributorName";
import { ISNI_SCHEMA_URI } from "@/utils/contributor-utils/contributor-identifier";

interface ContributorWithStatus extends Contributor {
  uuid: string;
  status: string;
}

const ContributorItemView = memo(
  ({
    contributor,
    orcidData,
    i,
  }: {
    contributor: ContributorWithStatus;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    orcidData?: any;
    i: number;
  }) => {
    // Bug fix: ORCID's authenticated/unauthenticated icon and status text
    // don't apply to ISNI - ISNI has no OAuth authentication flow, so
    // showing them for an ISNI contributor is meaningless/misleading.
    const isIsni = contributor.schemaUri === ISNI_SCHEMA_URI;

    // RAID-920: resolve the contributor's display name live - the "Name"
    // field below isn't just the dead orcidData prop (its real fetch is
    // commented out in ContributorsView, independent of this).
    const resolvedName = useResolvedContributorName(contributor.id, isIsni);

    return (
      <Stack gap={2}>
        <Typography variant="body1">Contributor #{i + 1}</Typography>

        {!isIsni && (
          <Stack direction="row" alignItems="center" gap={1}>
            <OrcidButton contributor={contributor} orcidData={orcidData} />
          </Stack>
        )}

        <Grid container spacing={2}>
          <DisplayItem label={isIsni ? "ISNI" : "ORCID"} value={contributor.id} width={4} />

          <DisplayItem label="Name" value={resolvedName} width={4} testid="contributor-name-display" />

          <DisplayItem
            label="Leader"
            value={contributor.leader ? "Yes" : "No"}
            width={2}
          />
          <DisplayItem
            label="Contact"
            value={contributor.contact ? "Yes" : "No"}
            width={2}
          />
        </Grid>

        <Stack gap={2} sx={{ pl: 3 }}>
          <Stack direction="row" alignItems="baseline">
            <Typography variant="body1" sx={{ mr: 0.5 }}>
              Roles
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Contributor #{i + 1}
            </Typography>
          </Stack>
          <Grid container spacing={1}>
            {contributor?.role ? (
              contributor.role
                .sort((a, b) => a.id.localeCompare(b.id))
                .map((role, index) => (
                  <ContributorRoleItemView
                    key={`contributor-${i}-role-${index}`}
                    contributorRole={role}
                  />
                ))
            ) : (
              <Grid item xs={12}>
                <Skeleton variant="rectangular" height={40} />
              </Grid>
            )}
          </Grid>

          <Stack direction="row" alignItems="baseline">
            <Typography variant="body1" sx={{ mr: 0.5 }}>
              Positions
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Contributor #{i + 1}
            </Typography>
          </Stack>
          <Stack gap={2} divider={<Divider />}>
            {contributor?.position ? (
              contributor.position.map((position, index) => (
                <ContributorPositionItemView
                  key={`contributor-${i}-position-${index}`}
                  contributorPosition={position}
                />
              ))
            ) : (
              <Skeleton variant="rectangular" height={60} />
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  }
);

ContributorItemView.displayName = "ContributorItemView";

export { ContributorItemView };
